import { useMemo } from 'react'
import findLastIndex from 'lodash/findLastIndex'
import { flatTreeToOneLevel, visitTree } from '@/utils/tree'

import { genColumnKey, splitColumns, getTrueColumns, filterColumns } from '../utils'

// ali table lock属性为布尔值，需要知道固定在左侧还是右侧
const getFixedFn = ({ columns }) => {
    const hasLock = columns.some(v => typeof v.lock === 'boolean')

    if (hasLock) {
        const ltIndex = columns.findIndex(v => !v.lock)
        const rtIndex = findLastIndex(columns, v => !v.lock)

        return (column, index) => {
            if (column.lock) {
                if (index < ltIndex) {
                    return 'left'
                }

                if (index > rtIndex) {
                    return 'right'
                }
            }

            return ''
        }
    }

    return column => {
        if (column.fixed === true) {
            return 'left'
        }

        return column.fixed
    }
}

// 优化顺序
const getSort = ({ config, index, sourceColumns }) => {
    if (!config) {
        return index
    }

    const getConfig = k => {
        const column = sourceColumns[k]

        const columnKey = genColumnKey(column.key || (column.dataIndex || column.code)?.toString(), k)

        return config[columnKey]
    }

    const anchorIndex = findLastIndex(sourceColumns.slice(0, index), (_, k) => {
        return getConfig(k)
    })

    // 在前面未找到存在配置的项
    if (anchorIndex === -1) {
        return 0
    }

    const anchorConfig = getConfig(anchorIndex)
    return typeof anchorConfig._sort === 'number' ? anchorConfig._sort : index
}

// lock为布尔值，需要转化为 'left'、'right'
const trans = ({ columns, config, defaultConfig, useAliTable, excludeKeys = [] }) => {
    const getFixed = getFixedFn({ columns, useAliTable })

    const attachConfig = ({ columns: inputColumns }) => {
        return inputColumns
            .map((v, k) => {
                const { defaultShow = true } = v
                const columnKey = genColumnKey(v.key || (v.dataIndex || v.code)?.toString(), k)
                const fixed = getFixed(v, k)
                const _show = defaultShow
                const setting =
                    config && config[columnKey]
                        ? {
                              ...(defaultConfig ? defaultConfig[columnKey] || {} : {}),
                              ...config[columnKey],
                              _width: config[columnKey]._width || v.width,
                          }
                        : {
                              _show,
                              _sort: getSort({ columnKey, config, column: v, index: k, sourceColumns: columns }),
                              _fixed: fixed,
                              _disabled: !!v.disabled,
                              _width: v.width,
                              ...(defaultConfig ? defaultConfig[columnKey] || {} : {}),
                          }

                const obj = {
                    ...v,
                    ...setting,
                    fixed,
                    index: k,
                    key: columnKey,
                }

                // 嵌套结构
                if (v.children?.length) {
                    const objTemp = {
                        ...obj,
                        children: attachConfig({ columns: v.children }),
                    }

                    if (objTemp.children?.filter(_ => _._show).length > 0) {
                        objTemp._show = true
                    } else {
                        objTemp._show = false
                    }
                    return objTemp
                }

                return obj
            })
            .filter(_ => !excludeKeys.includes(_.key))
    }

    const newColumns = attachConfig({ columns })

    const { leftColumns, centerColumns, rightColumns } = splitColumns(newColumns)
    const trueColumns = getTrueColumns(filterColumns([...leftColumns, ...centerColumns, ...rightColumns]))

    const observeSizes = sizes => {
        const fullColumns = [...leftColumns, ...centerColumns, ...rightColumns]

        const flatTrueColumns = flatTreeToOneLevel(trueColumns, {
            childrenPropName: 'children',
            skipHasChidlrenNode: true,
        })

        const newConfig = {}

        visitTree(fullColumns, (c, k) => {
            const target = (config && config[c.key]) || {}
            const sizeIndex = flatTrueColumns.findIndex(v => v.key === c.key)

            newConfig[c.key] = {
                ...target,
                _sort: k,
                _fixed: c._fixed,
                _show: c._show,
                _disabled: c._disabled,
                _width: sizes[sizeIndex],
            }
        })

        return {
            subscribe(callback) {
                callback(newConfig)
            },
        }
    }

    return {
        columns: trueColumns,
        leftColumns,
        centerColumns,
        rightColumns,
        observeSizes,
    }
}

export default ({ config, defaultConfig, columns, excludeKeys }) => {
    return useMemo(() => {
        return {
            sourceColumns: columns,
            config,
            defaultConfig,
            ...trans({ columns, config, defaultConfig, excludeKeys }),
        }
    }, [config, defaultConfig, columns])
}
